import { prop } from '@ipfabric/utils/fp/prop';
import type { GlobalStateMergedState } from 'components/GlobalState/GlobalStateModel';
import { selectAllRoutes } from 'components/GlobalState/selectors/selectAllRoutes';
import {
  type ApiScopeString,
  getApiScopeString,
  selectApiScopeStringSet,
} from 'components/GlobalState/selectors/selectApiScopeStringSet';
import type { RoutesTreeItem } from 'containers/App/Routes/RoutesTreeItem';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';
import { selectAuthenticatedAndLicensed } from './selectAuthenticatedAndLicensed';

const filterAllowedTableRoutes = (
  routes: RoutesTreeItem[],
  apiScopeStringSet: Set<ApiScopeString>,
): RoutesTreeItem[] =>
  routes.reduce<RoutesTreeItem[]>((acc, route) => {
    const children = route.children
      ? filterAllowedTableRoutes(route.children, apiScopeStringSet)
      : undefined;

    if (
      route.apiEndpoint &&
      !apiScopeStringSet.has(
        getApiScopeString({ method: 'POST', path: route.apiEndpoint }),
      )
    ) {
      return acc;
    }

    acc.push({
      ...route,
      children,
    });
    return acc;
  }, []);

/**
 * Select all routes except table routes user has no access to.
 *
 * @deprecated All routes are now propagated to react router and restricted with
 * RestrictedPageAccess component. This is still used by device explorer who loads
 * available tables tree this way.
 */
export const selectAllowedRoutes: (
  s: GlobalStateMergedState,
) => RoutesTreeItem[] = createScopedSelector(() =>
  createSelector(
    selectAllRoutes,
    selectApiScopeStringSet,
    prop('auth', 'admin'),
    selectAuthenticatedAndLicensed,
    (allRoutes, apiScopeStringSet, admin, authenticated) =>
      // Add all routes for unauthenticated user to allow proper login flow:
      // show login page and redirect to original route after login
      admin || !authenticated
        ? allRoutes
        : filterAllowedTableRoutes(allRoutes, apiScopeStringSet),
  ),
);
